import type { EntityBatchSyncStatus } from './entityMutationSync'
import type { EntityMutationSyncState } from './entityMutationSync'

// Reconciles the per-mutation `syncState` returned by an individual save
// against the global outbox status reported by the surface-wide sync panel.
//
// Closes v2.1 findings #21 and #24: previously, the per-mutation success
// toast ("Biomass log synced" / "Created lot 2.0 X and synced the change.")
// rendered as soon as THAT specific row's sync resolved, even if the global
// outbox still held queued or blocked items belonging to other rows or
// earlier retries. That contradicted the surface's sync panel which keeps
// reporting "queued and still pending sync" until the queue drains.
//
// The rule is intentionally conservative: only downgrade `synced` to a
// less-optimistic state when the global outbox actually has pending or
// blocked work; otherwise pass the per-mutation state through unchanged.

export type ReconcileSyncMutationInput = {
  // The per-mutation sync result reported by the local save (entityMutationSync).
  mutationSyncState: EntityMutationSyncState
  // The current surface-wide outbox status. Pass `null` when the surface
  // does not (yet) track a queue status — in that case there is nothing
  // to reconcile against.
  outboxStatus: EntityBatchSyncStatus | null | undefined
}

export const reconcileSyncMutationState = (input: ReconcileSyncMutationInput): EntityMutationSyncState => {
  // If the per-mutation sync did not claim 'synced', nothing to downgrade.
  if (input.mutationSyncState !== 'synced') return input.mutationSyncState

  const outbox = input.outboxStatus
  if (!outbox) return 'synced'

  // Blocked items on the queue (HTTP 4xx errors etc.) imply pending work
  // that needs attention. Report 'pending' so the per-mutation toast does
  // not claim 'synced' while the panel surfaces a blocker.
  if (outbox.blockedCount > 0) return 'pending'

  // If the outbox itself reports pending or offline, mirror that state so
  // the toast and the panel agree.
  if (outbox.syncState === 'pending') return 'pending'
  if (outbox.syncState === 'offline') return 'offline'

  return 'synced'
}
